import { useLazyQuery } from '@apollo/client'
import axios from 'axios'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { FaInbox, FaSort } from 'react-icons/fa'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  GET_BOOKING_BY_INCREMENTAL_ID,
  GET_FILTERED_CAB_BOOKING,
} from '../../Services/cabggl'
import { getDecryptedSessionStorageVariable } from '../../utils/encryptStorage'
import { SkyOutlinedButton } from '../../Components/Buttons'
import { getRelievingDate, getReportingDate } from '../../utils/datesfunctions'

export default function SearchCabBooking() {
  const [selectedSize, setSelectedSize] = useState(10)
  const [pageNumber, setPageNumber] = useState(1)
  const [total, setTotal] = useState(0)
  const [sortedField, setSortedField] = useState(null)
  const [selectedBooking, setSelectedBooking] = useState(false)
  const [booking, setBooking] = useState([])
  const [sortOrder, setSortOrder] = useState('')
  const [updateCabBooking, setUpdateCabBooking] = useState(false)
  const [searchParam] = useSearchParams()
  const searchBy = searchParam.get('searchBy')
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const bookingId = queryParams.get('bookingId')
  const navigate = useNavigate()

  const [bookingData, { loading, refetch: refetchByBookingId }] = useLazyQuery(
    GET_BOOKING_BY_INCREMENTAL_ID
  )
  const [
    filterData,
    { loading: filterLoading, refetch: refetchByBookingData },
  ] = useLazyQuery(GET_FILTERED_CAB_BOOKING)

  useEffect(() => {
    getData()
  }, [pageNumber, selectedSize])
  const getSortedBookings = () => {
    const sortedBookings = [...booking] // Make a copy to avoid mutating state directly

    if (sortedField) {
      sortedBookings.sort((a, b) => {
        // Handle ascending/descending sorting
        const fieldA = a[sortedField]
        const fieldB = b[sortedField]

        // Handle undefined or null values (consider them as last in sorting)
        if (fieldA === undefined || fieldA === null)
          return sortOrder === 'asc' ? 1 : -1
        if (fieldB === undefined || fieldB === null)
          return sortOrder === 'asc' ? -1 : 1

        if (typeof fieldA === 'string' && typeof fieldB === 'string') {
          return sortOrder === 'asc'
            ? fieldA.localeCompare(fieldB)
            : fieldB.localeCompare(fieldA)
        }
        return sortOrder === 'asc' ? fieldA - fieldB : fieldB - fieldA
      })
    }

    return sortedBookings
  }

  useEffect(() => {
    setBooking(getSortedBookings())
  }, [sortedField, sortOrder])

  const getData = async () => {
    if (searchBy === 'bookingId') {
      await bookingData({
        variables: {
          id: bookingId,
        },
        fetchPolicy: 'no-cache',
      }).then((res) => {
        if (res?.data?.getBookingByIncrementalId) {
          setBooking([res?.data?.getBookingByIncrementalId])
        }
      })
    } else if (searchBy === 'bookingDate') {
      const obj = location.state
      await filterData({
        variables: {
          filterCabInput: {
            ...obj,
            operationCity:
              getDecryptedSessionStorageVariable('operationCity') === 'All'
                ? ''
                : getDecryptedSessionStorageVariable('operationCity'),
          },
          pageNumber,
          pageSize: selectedSize,
          fetchPolicy: 'no-cache',
        },
        fetchPolicy: 'no-cache',
      }).then((res) => {
        if (res?.data?.getFilteredCabBooking) {
          setBooking(res?.data?.getFilteredCabBooking.cabBooking)
          setTotal(res?.data?.getFilteredCabBooking.total)
          setSelectedBooking(
            (prev) =>
              res.data?.getFilteredCabBooking?.cabBooking?.filter(
                (s) => s?.id === prev?.id
              )?.[0]
          )
          refetch()
        }
      })
    }
  }

  useEffect(() => {
    getData()
  }, [pageNumber, selectedSize])

  const refetch = () => {
    refetchByBookingData()
    refetchByBookingId()
  }

  const handleSort = (field) => {
    if (!field) return
    if (field === sortedField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortedField(field)
      setSortOrder('asc')
    }
  }

  const handleNavigateBack = () => {
    navigate(-1)
  }

  return (
    <div className="w-full  h-full bg-white p-4 shadow rounded-md text-start">
      <SkyOutlinedButton label={'Back'} onClick={handleNavigateBack} />
      <div className="w-full flex justify-between">
        <div className="text-stone-500 text-xl font-bold">Booking History</div>
      </div>

      <div className="mt-4 w-full overflow-scroll h-[70vh]">
        <table className="text-sm text-left rtl:text-right text-gray-500 border-collapse border border-#cedbe2-800 ">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky  top-0 ">
            <tr>
              <th scope="col" className=" py-3 border border-#cedbe2-800">
                <div className="flex items-center">
                  Booking Id <FaSort onClick={() => handleSort('id')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Customer Name{' '}
                  <FaSort onClick={() => handleSort('customerId.firstName')} />
                </div>
              </th>

              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Travel Type
                  <FaSort onClick={() => handleSort('travelType')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Trip Type
                  <FaSort onClick={() => handleSort('tripType')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Car Type
                  <FaSort onClick={() => handleSort('carType')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center w-48">
                  Reporting Location
                  <FaSort onClick={() => handleSort('pickAddress')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  {' '}
                  Time
                  <FaSort onClick={() => handleSort('reportingDate')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Relieving Date Time
                  <FaSort
                    onClick={() => handleSort('tentativeRelievingDate')}
                  />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800 ">
                <div className="flex gap-1 items-center">
                  Status
                  <FaSort onClick={() => handleSort('status')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Fleet Owner Name
                  <FaSort onClick={() => handleSort(`driverId.firstName`)} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Car No
                  <FaSort onClick={() => handleSort(`driverId.firstName`)} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Driver Name
                  <FaSort onClick={() => handleSort(`driverId.firstName`)} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  Driver Share
                  <FaSort onClick={() => handleSort('estimatedDriverShare')} />
                </div>
              </th>
              <th scope="col" className="px-2 py-3 border border-#cedbe2-800 ">
                <div className="flex gap-1 items-center">
                  Id Share <FaSort onClick={() => handleSort('idShare')} />
                </div>
              </th>

              <th scope="col" className="px-2 py-3 border border-#cedbe2-800">
                <div className="flex gap-1 items-center">
                  OTP <FaSort onClick={() => handleSort('otp')} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className=" w-full border border-#cedbe2-800 ">
            {booking ? (
              booking?.slice().map((booking, index) => (
                <tr
                  className={`${
                    booking?.customerId?.id === booking?.createdBy
                      ? 'bg-[#f3c74a]'
                      : booking?.paymentMethod === 'D'
                      ? 'bg-[#cedbe2]'
                      : booking?.paymentMethod === 'O'
                      ? 'bg-[#8ab8f1]'
                      : ''
                  } border-b`}
                  key={index}
                  onClick={() => {
                    setUpdateCabBooking(true)
                    setSelectedBooking(booking)
                  }}
                >
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.id}
                  </td>
                  <td
                    onClick={(e) => {
                      e.stopPropagation()
                      axios.post(
                        `https://in1-ccaas-api.ozonetel.com/CAServices/AgentManualDial.php?api_key=KK2ad106a9229d0da20c5dfb077f208110&username=indiandrivers&agentID=${getDecryptedSessionStorageVariable(
                          'email'
                        )}&campaignName=Inbound_912245811450&customerNumber=${
                          booking?.customerId?.mobileNumber
                        }&uui=test&UCID=true&format=json`
                      )
                    }}
                    className="px-2 py-4 border border-#cedbe2-800 cursor-pointer"
                  >
                    <div className="flex gap-1 items-center">
                      <div>
                        {booking?.firstTimeUser && (
                          <div className="w-3 h-3 rounded-full bg-green-700 animate-blink" />
                        )}
                      </div>

                      <div className="w-full">
                        {booking?.customerId?.firstName +
                          ' ' +
                          booking.customerId?.lastName +
                          ' ' +
                          '(' +
                          booking?.customerId?.mobileNumber +
                          ')'}
                      </div>
                    </div>
                  </td>

                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.travelType}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.tripType}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.carType}
                  </td>

                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.pickAddress?.address}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {moment(getReportingDate(booking)).format('LLL')}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {getRelievingDate(booking)}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.status === 'Start Duty'
                      ? 'On Duty'
                      : booking?.status === 'Off Duty'
                      ? 'Done'
                      : booking?.status}
                  </td>
                  <td
                    onClick={(e) => {
                      e.stopPropagation()
                      axios.post(
                        `https://in1-ccaas-api.ozonetel.com/CAServices/AgentManualDial.php?api_key=KK2ad106a9229d0da20c5dfb077f208110&username=indiandrivers&agentID=${getDecryptedSessionStorageVariable(
                          'email'
                        )}&campaignName=Inbound_912245811450&customerNumber=${
                          booking.fleetOwnerId.mobileNumber
                        }&uui=test&UCID=true&format=json`
                      )
                    }}
                    className="px-2 py-4 border border-#cedbe2-800 cursor-pointer"
                  >
                    {`${booking.fleetOwnerId?.firstName ?? ''} ${
                      booking.fleetOwnerId?.lastName ?? ''
                    } ${
                      booking.fleetOwnerId?.mobileNumber
                        ? `(${booking.fleetOwnerId.mobileNumber})`
                        : ''
                    }`.trim()}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800 ">
                    {booking.cabId?.variant && booking.cabId?.carNo
                      ? `${booking.cabId?.variant ?? ''} (${
                          booking.cabId?.carNo ?? ''
                        })`.trim()
                      : ''}
                  </td>
                  <td
                    className="px-2 py-4 border border-#cedbe2-800 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation()
                      axios.post(
                        `https://in1-ccaas-api.ozonetel.com/CAServices/AgentManualDial.php?api_key=KK2ad106a9229d0da20c5dfb077f208110&username=indiandrivers&agentID=${getDecryptedSessionStorageVariable(
                          'email'
                        )}&campaignName=Inbound_912245811450&customerNumber=${
                          booking?.driverId?.mobileNumber
                        }&uui=test&UCID=true&format=json`
                      )
                    }}
                  >
                    {`${booking.driverId?.firstName ?? ''} ${
                      booking.driverId?.lastName ?? ''
                    } ${
                      booking.driverId?.mobileNumber
                        ? `(${booking.driverId.mobileNumber})`
                        : ''
                    }`.trim()}
                  </td>

                  <td className="px-2 py-4 border border-#cedbe2-800">
                    ₹{parseFloat(booking.faresId.fleetShare).toFixed(2)}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    ₹{parseFloat(booking.faresId.idShare).toFixed(2)}
                  </td>
                  <td className="px-2 py-4 border border-#cedbe2-800">
                    {booking?.otp ? booking?.otp : ''}
                  </td>
                </tr>
              ))
            ) : (
              <tr className="bg-white">
                <td
                  className="px-2 py-4 border border-#cedbe2-800 text-center"
                  colSpan="13"
                >
                  <div className="flex justify-center items-center h-16">
                    <FaInbox className="mr-2" />
                    No data found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* {total !== 0 && searchBy === 'bookingDate' && (
        <div className="flex item-center justify-between ">
          <Pagination
            selectedSize={selectedSize}
            setPageNumber={setPageNumber}
            pageNumber={pageNumber}
            total={total}
          />
          <SelectPageDropDown
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
          />
        </div>
      )}

      {updateCabBooking && (
        <UpdateCabBooking
          isOpen={updateCabBooking}
          onClose={setUpdateCabBooking}
          refetch={getData}
          selectedBooking={selectedBooking}
        />
      )} */}
    </div>
  )
}
